// ============================================
// Phân trang phía client cho danh sách đã nằm sẵn trong bộ nhớ.
//
// Dùng cho các bảng của trang Reports: dữ liệu do một query trả về trọn gói
// (top products, lost items, profit theo cửa hàng), không có endpoint phân trang
// riêng. Gom logic vào một chỗ để 3 bảng không mỗi bảng một kiểu lệch nhau.
// ============================================

import { useEffect, useMemo, useState } from 'react';

export interface ClientPagination<T> {
  /** Các phần tử của trang hiện tại. */
  pageItems: T[];
  /** Trang hiện tại (1-indexed), đã kẹp trong khoảng hợp lệ. */
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  setPage: (page: number) => void;
  /**
   * Có dữ liệu để hiển thị thanh phân trang không.
   *
   * Cố ý là "có ít nhất 1 dòng" chứ KHÔNG phải "nhiều hơn 1 trang": thanh này
   * còn mang dòng "Showing 5 of 20" — thông tin vẫn có ích khi danh sách chưa
   * đầy một trang, và giữ cho mọi bảng trong app hiển thị giống nhau.
   */
  hasItems: boolean;
}

export function useClientPagination<T>(items: T[], pageSize = 10): ClientPagination<T> {
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Đổi bộ lọc/khoảng ngày làm danh sách ngắn lại — nếu đang đứng ở trang đã
  // biến mất thì kéo về trang cuối còn tồn tại, tránh bảng trống dù có dữ liệu.
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageItems = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize]
  );

  return {
    pageItems,
    currentPage: safePage,
    totalPages,
    totalItems,
    pageSize,
    setPage: setCurrentPage,
    hasItems: totalItems > 0,
  };
}
